import { api, downloadFile, downloadGetFile, fetchTextFile, request } from './client';
import type {
  Acceptance,
  AcceptDecision,
  AgentPlatform,
  Asset,
  AssetLifecycleEvent,
  AssetCategory,
  AssetModel,
  AssetScoresReport,
  AssetType,
  CustomFieldDefinition,
  AssetUpsert,
  Assignment,
  AssignmentAction,
  AuditChainReport,
  AuditEvent,
  Consumable,
  ConsumableMovement,
  CompleteWorkOrder,
  ContractFields,
  ContractKind,
  ContractStanding,
  ContractSummary,
  CreateContract,
  CreateSchedule,
  Downtime,
  MaintenanceSchedule,
  MaintenanceSummary,
  RaiseWorkOrder,
  RenewContract,
  ServiceContract,
  UpdateSchedule,
  WorkOrder,
  WorkOrderFields,
  WorkOrderKind,
  WorkOrderStatus,
  CreateAssignment,
  CreateOrder,
  MovementReason,
  CurrentUser,
  Profile,
  Department,
  DeploymentStatus,
  DeviceGroup,
  DeviceGroupDetail,
  DevicePolicies,
  Employee,
  EndpointDetail,
  EndpointDevice,
  EndpointFactSection,
  InstalledSoftware,
  SoftwareChange,
  SoftwareTitle,
  SoftwareInstall,
  SoftwareVersionCount,
  FleetSoftwareChange,
  DeviceLocation,
  FleetLocation,
  LocationTrackingSettings,
  LocationTrackingRequest,
  EndpointSummary,
  EnrollSecret,
  IssuedEnrollSecret,
  LifecycleRule,
  Location,
  Manufacturer,
  Notification,
  Organization,
  OutboxMessage,
  OutboxStats,
  Page,
  PageParams,
  Policy,
  PolicyDeployment,
  PolicyDeployResult,
  PolicyEvent,
  PolicyInput,
  PolicySettings,
  PolicyStatus,
  PolicySummary,
  PolicyValidation,
  PolicyVersion,
  PolicyVersionSummary,
  PreparationRun,
  ProcurementOrder,
  ProcurementReceipt,
  RecordTask,
  ReceiveInput,
  RejectDecision,
  RejectionReason,
  StartRun,
} from './types';

/**
 * Every backend call the application makes, in one place, grouped by domain.
 *
 * Each function corresponds to exactly one implemented endpoint. There is no function here without
 * a server route behind it: a screen either uses real data or it does not exist.
 */

// ---- Identity ---------------------------------------------------------------------------------

export const identityApi = {
  me: () => api.get<CurrentUser>('/api/v1/me'),

  /** Designation, roles and manager, for the profile menu. */
  profile: () => api.get<Profile>('/api/v1/me/profile'),

  /** The caller's own interface preferences - an opaque document the server stores for them. */
  preferences: () => api.get<Record<string, unknown>>('/api/v1/me/preferences'),

  savePreferences: (preferences: Record<string, unknown>) =>
    api.put<Record<string, unknown>>('/api/v1/me/preferences', preferences),

  /**
   * Development-only token issuer. Present only when the backend runs with the `local` profile,
   * which refuses to start in production - so this call simply fails everywhere else.
   */
  localToken: (subject: string) =>
    api.post<{ access_token: string; token_type: string; expires_at: string }>(
      '/api/local-tokens',
      { subject },
    ),

  /**
   * Sends a sign-in code after checking the provisioned account's password.
   */
  requestSignInCode: (email: string, password: string) =>
    api.post<{ validitySeconds: number }>('/api/auth/otp/request', { email, password }),

  /** Requests a generic password setup or reset email for a provisioned account. */
  requestPasswordLink: (email: string) =>
    api.post<{ message: string }>('/api/auth/password/request', { email }),

  /** Completes password setup or reset without creating an authenticated session. */
  completePassword: (token: string, password: string) =>
    api.post<{ message: string }>('/api/auth/password/complete', { token, password }),

  /** Exchanges a correct code for an access token. */
  verifySignInCode: (email: string, password: string, code: string) =>
    api.post<{
      token: string;
      tokenType: string;
      expiresInSeconds: number;
      displayName: string | null;
      idleTimeoutMinutes?: number;
      maxSessionMinutes?: number;
    }>('/api/auth/otp/verify', { email, password, code }),

  /** Renews the access token while the person is active; fails once the session is over. */
  renewSession: () =>
    api.post<{
      token: string;
      tokenType: string;
      expiresInSeconds: number;
      idleTimeoutMinutes?: number;
      maxSessionMinutes?: number;
    }>('/api/auth/session/refresh'),

  /**
   * Revokes the token being used, server-side.
   *
   * Clearing the browser's copy ends nothing on its own: a token is accepted because it is
   * signed, so a copy taken beforehand would keep working until it expired. Sessions on other
   * devices are untouched.
   */
  endSession: () => api.post<{ message: string }>('/api/auth/session/logout'),
};

// ---- Organisation -----------------------------------------------------------------------------

export const organizationApi = {
  current: () => api.get<Organization>('/api/v1/organization'),
  departments: () => api.get<Department[]>('/api/v1/organization/departments'),
  locations: () => api.get<Location[]>('/api/v1/organization/locations'),
};

// ---- Catalogue (reference data) ---------------------------------------------------------------

export const catalogApi = {
  assetTypes: () => api.get<AssetType[]>('/api/v1/catalog/asset-types'),
  assetModels: (assetTypeId?: string) =>
    api.get<AssetModel[]>('/api/v1/catalog/asset-models', { query: { assetTypeId } }),
  manufacturers: () => api.get<Manufacturer[]>('/api/v1/catalog/manufacturers'),
  vendors: () =>
    api.get<{ id: string; code: string; name: string; contactEmail: string | null; active: boolean }[]>(
      '/api/v1/catalog/vendors',
    ),
  lifecycleRules: () => api.get<LifecycleRule[]>('/api/v1/catalog/lifecycle-rules'),
};

// ---- Administration (Settings) ----------------------------------------------------------------

export interface AdminDepartment {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

export interface AdminLocation {
  id: string;
  code: string;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  countryCode: string | null;
  postalCode: string | null;
  active: boolean;
}

export interface LocationAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  countryCode?: string;
  postalCode?: string;
}

export interface AdminVendor {
  id: string;
  code: string;
  name: string;
  contactEmail: string | null;
  contactPhone: string | null;
  active: boolean;
}

export interface AssetTypeFields {
  name: string;
  category: string;
  requiresEndpointAgent: boolean;
  requiresMdmEnrolment: boolean;
  requiresAssetTag: boolean;
  requiresSanitisation: boolean;
  usefulLifeMonths?: number;
  salvagePercent?: number;
  customFieldSchema?: CustomFieldDefinition[] | null;
}

export interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  employeeId: string | null;
  enabled: boolean;
  disabledReason: string | null;
  lastLoginAt: string | null;
  roles: { id: string; code: string; name: string }[];
  /** `ENTRA` when the account comes from Active Directory; null for a local account. */
  directorySource: string | null;
  directorySyncedAt: string | null;
}

export interface DirectorySyncRun {
  id: string;
  trigger: 'SCHEDULED' | 'MANUAL';
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  startedAt: string;
  finishedAt: string | null;
  usersChecked: number;
  usersChanged: number;
  usersFailed: number;
  failureReason: string | null;
}

export interface DirectoryStatus {
  configured: boolean;
  directoryName: string;
  mode: 'GRAPH' | 'MOCK';
  scheduledSync: boolean;
  syncIntervalMinutes: number;
  lastRun: DirectorySyncRun | null;
}

/** An account as Active Directory holds it, read live. */
export interface DirectoryAccount {
  objectId: string;
  userPrincipalName: string | null;
  displayName: string | null;
  mail: string | null;
  jobTitle: string | null;
  department: string | null;
  accountEnabled: boolean;
  /** Mastered in on-premises AD: its enabled state can only be changed there. */
  onPremisesSynced: boolean;
}

export interface AdminRole {
  id: string;
  code: string;
  name: string;
  description: string | null;
  systemRole: boolean;
  permissions: string[];
}

/** A part of the product, and whether one company has it. */
export interface TenantModule {
  code: string;
  label: string;
  description: string;
  /** Every company has it, whatever the plan says. */
  mandatory: boolean;
  enabled: boolean;
}

export interface PermissionEntry {
  code: string;
  category: string;
  description: string;
}

/** A role as one company sees it: the built-in ones, and the ones it wrote itself. */
export interface CompanyRole {
  id: string;
  code: string;
  name: string;
  description: string | null;
  /** Shared by every company and not editable here. */
  builtIn: boolean;
  active: boolean;
  holders: number;
  permissions: string[];
  createdAt: string;
}

/**
 * Settings' write side. Reads for pickers stay on `organizationApi` and `catalogApi`, which list
 * active entries only; these list everything, inactive included, because that is what managing them
 * needs.
 */
export const adminApi = {
  directoryStatus: () => api.get<DirectoryStatus>('/api/v1/admin/directory'),
  syncDirectory: () => api.post<DirectorySyncRun>('/api/v1/admin/directory/sync'),
  directoryAccount: (userId: string) =>
    api.get<DirectoryAccount>(`/api/v1/admin/users/${userId}/directory`),
  refreshFromDirectory: (userId: string) =>
    api.post<AdminUser>(`/api/v1/admin/users/${userId}/directory/refresh`),

  departments: () => api.get<AdminDepartment[]>('/api/v1/admin/organization/departments'),
  createDepartment: (body: { code: string; name: string }) =>
    api.post<AdminDepartment>('/api/v1/admin/organization/departments', body),
  updateDepartment: (id: string, body: { name: string; active: boolean }) =>
    api.put<AdminDepartment>(`/api/v1/admin/organization/departments/${id}`, body),

  locations: () => api.get<AdminLocation[]>('/api/v1/admin/organization/locations'),
  createLocation: (body: { code: string; name: string; address?: LocationAddress }) =>
    api.post<AdminLocation>('/api/v1/admin/organization/locations', body),
  updateLocation: (id: string, body: { name: string; address?: LocationAddress; active: boolean }) =>
    api.put<AdminLocation>(`/api/v1/admin/organization/locations/${id}`, body),

  allAssetTypes: () =>
    api.get<AssetType[]>('/api/v1/catalog/asset-types', { query: { activeOnly: false } }),
  createAssetType: (body: { code: string; fields: AssetTypeFields }) =>
    api.post<AssetType>('/api/v1/admin/catalog/asset-types', body),
  updateAssetType: (id: string, body: { fields: AssetTypeFields; active: boolean; expectedVersion?: number }) =>
    api.put<AssetType>(`/api/v1/admin/catalog/asset-types/${id}`, body),

  createAssetModel: (body: {
    manufacturerId: string;
    assetTypeId: string;
    modelName: string;
    modelNumber?: string;
    specification?: string;
  }) => api.post<AssetModel>('/api/v1/admin/catalog/asset-models', body),
  updateAssetModel: (
    id: string,
    body: { modelName: string; modelNumber?: string; specification?: string; active: boolean },
  ) => api.put<AssetModel>(`/api/v1/admin/catalog/asset-models/${id}`, body),

  createManufacturer: (body: { name: string; active: boolean }) =>
    api.post<Manufacturer>('/api/v1/admin/catalog/manufacturers', body),
  updateManufacturer: (id: string, body: { name: string; active: boolean }) =>
    api.put<Manufacturer>(`/api/v1/admin/catalog/manufacturers/${id}`, body),

  vendors: () => api.get<AdminVendor[]>('/api/v1/admin/catalog/vendors'),
  createVendor: (body: { code: string; name: string; contactEmail?: string; contactPhone?: string }) =>
    api.post<AdminVendor>('/api/v1/admin/catalog/vendors', body),
  updateVendor: (
    id: string,
    body: { name: string; contactEmail?: string; contactPhone?: string; active: boolean },
  ) => api.put<AdminVendor>(`/api/v1/admin/catalog/vendors/${id}`, body),

  users: (params: PageParams & { search?: string }) =>
    api.get<Page<AdminUser>>('/api/v1/admin/users', { query: { ...params } }),
  roles: () => api.get<AdminRole[]>('/api/v1/admin/roles'),
  permissions: () => api.get<PermissionEntry[]>('/api/v1/admin/permissions'),

  /** The roles this company can assign: the built-in ones, plus its own. */
  companyRoles: () => api.get<CompanyRole[]>('/api/v1/admin/company-roles'),

  /** What a company may put in a role of its own: the catalogue without platform permissions. */
  assignablePermissions: () =>
    api.get<{ permissions: string[] }>('/api/v1/admin/company-roles/assignable-permissions'),

  createCompanyRole: (body: { name: string; description?: string; permissions: string[] }) =>
    api.post<CompanyRole>('/api/v1/admin/company-roles', body),

  updateCompanyRole: (
    roleId: string,
    body: { name: string; description?: string; permissions: string[] },
  ) => api.put<CompanyRole>(`/api/v1/admin/company-roles/${roleId}`, body),

  deactivateCompanyRole: (roleId: string) =>
    api.post<CompanyRole>(`/api/v1/admin/company-roles/${roleId}/deactivate`),

  reactivateCompanyRole: (roleId: string) =>
    api.post<CompanyRole>(`/api/v1/admin/company-roles/${roleId}/reactivate`),

  provisionUser: (body: {
    displayName: string;
    email: string;
    roleIds: string[];
    /** Optional: the employee record of the same person, tied at creation. */
    employeeId?: string;
  }) => api.post<AdminUser>('/api/v1/admin/users', body),
  changeRoles: (userId: string, roleIds: string[]) =>
    api.put<AdminUser>(`/api/v1/admin/users/${userId}/roles`, { roleIds }),

  /**
   * Ties an account to the employee record of the same person, or unties it with null.
   *
   * Two different things: an employee is somebody assets are given to, a user is somebody
   * who signs in. Where one person is both, this is what says so.
   */
  linkEmployee: (userId: string, employeeId: string | null) =>
    api.put<AdminUser>(`/api/v1/admin/users/${userId}/employee`, { employeeId }),
  disableUser: (userId: string, reason?: string) =>
    api.post<AdminUser>(`/api/v1/admin/users/${userId}/disable`, { reason }),
  enableUser: (userId: string) => api.post<AdminUser>(`/api/v1/admin/users/${userId}/enable`),

  getSessionPolicy: () => api.get<SessionPolicy>('/api/v1/admin/organization/session-policy'),
  sessionPolicy: () => api.get<SessionPolicy>('/api/v1/admin/organization/session-policy'),
  updateSessionPolicy: (body: SessionPolicy) =>
    api.put<SessionPolicy>('/api/v1/admin/organization/session-policy', body),
};

export interface SessionPolicy {
  idleTimeoutMinutes: number;
  maxSessionMinutes: number;
}

// ---- Platform: companies, plans, subscriptions and billing ------------------------------------

export type BillingInterval = 'MONTHLY' | 'ANNUAL';
export type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';
export type InvoiceStatus = 'ISSUED' | 'PAID' | 'VOID';

export interface Plan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  billingInterval: BillingInterval;
  pricePerSeat: number;
  currencyCode: string;
  /** Null means the plan does not cap assets. */
  maxAssets: number | null;
  active: boolean;
}

export interface Subscription {
  id: string;
  status: SubscriptionStatus;
  seats: number;
  trialEndsOn: string | null;
  trialExpired: boolean;
  /** First day of the period that will be billed next. */
  currentPeriodStart: string;
  /** Day after the last day of that period. */
  currentPeriodEnd: string;
  statusReason: string | null;
  cancelledAt: string | null;
  plan: Plan | null;
}

/** A null limit means unlimited. */
export interface LicenceUsage {
  seatsInUse: number;
  seatLimit: number | null;
  assetsUnderManagement: number;
  assetLimit: number | null;
}

export interface Tenant {
  organizationId: string;
  code: string;
  name: string;
  active: boolean;
  createdAt: string;
  accessAllowed: boolean;
  /** Null when the company is unlicensed, which means unrestricted. */
  subscription: Subscription | null;
  usage: LicenceUsage;
  overdueInvoices: number;
}

export interface Invoice {
  id: string;
  organizationId: string;
  invoiceNumber: string;
  planName: string;
  periodStart: string;
  periodEnd: string;
  seats: number;
  unitPrice: number;
  amount: number;
  currencyCode: string;
  status: InvoiceStatus;
  overdue: boolean;
  issuedAt: string;
  dueOn: string;
  paidAt: string | null;
  paymentReference: string | null;
  voidReason: string | null;
}

export interface PlatformSummary {
  companies: number;
  unlicensedCompanies: number;
  subscriptionsByStatus: Partial<Record<SubscriptionStatus, number>>;
  seatsSold: number;
  overdueInvoices: number;
  outstanding: { currencyCode: string; invoices: number; amount: number }[];
}

export interface SubscriptionTerms {
  planId: string;
  seats: number;
  /** 0 or absent: paying from today. */
  trialDays?: number;
}

export interface PlanTerms {
  name: string;
  description?: string;
  billingInterval: BillingInterval;
  pricePerSeat: number;
  currencyCode: string;
  maxAssets?: number | null;
}

const PLATFORM_TENANTS = '/api/v1/platform/tenants/';

export const platformApi = {
  summary: () => api.get<PlatformSummary>('/api/v1/platform/summary'),

  tenants: (params: PageParams & { search?: string }) =>
    api.get<Page<Tenant>>('/api/v1/platform/tenants', { query: { ...params } }),
  tenant: (organizationId: string) => api.get<Tenant>(`/api/v1/platform/tenants/${organizationId}`),
  createTenant: (body: {
    code: string;
    name: string;
    subscription: SubscriptionTerms;
    administrator: { displayName: string; email: string };
  }) => api.post<Tenant>('/api/v1/platform/tenants', body),

  /** Every module of the product, each marked with whether this company has it. */
  tenantModules: (organizationId: string) =>
    api.get<TenantModule[]>(`${PLATFORM_TENANTS}${organizationId}/modules`),

  /** Replaces what a company has. Mandatory modules are added whether listed or not. */
  changeTenantModules: (organizationId: string, modules: string[]) =>
    api.put<TenantModule[]>(`${PLATFORM_TENANTS}${organizationId}/modules`, { modules }),

  subscribe: (organizationId: string, body: SubscriptionTerms) =>
    api.post<Tenant>(`/api/v1/platform/tenants/${organizationId}/subscription`, body),
  changePlan: (organizationId: string, planId: string) =>
    api.put<Tenant>(`/api/v1/platform/tenants/${organizationId}/subscription/plan`, { planId }),
  changeSeats: (organizationId: string, seats: number) =>
    api.put<Tenant>(`/api/v1/platform/tenants/${organizationId}/subscription/seats`, { seats }),
  activate: (organizationId: string) =>
    api.post<Tenant>(`/api/v1/platform/tenants/${organizationId}/subscription/activate`),
  suspend: (organizationId: string, reason: string) =>
    api.post<Tenant>(`/api/v1/platform/tenants/${organizationId}/subscription/suspend`, { reason }),
  reactivate: (organizationId: string) =>
    api.post<Tenant>(`/api/v1/platform/tenants/${organizationId}/subscription/reactivate`),
  cancel: (organizationId: string, reason: string) =>
    api.post<Tenant>(`/api/v1/platform/tenants/${organizationId}/subscription/cancel`, { reason }),

  invoices: (organizationId: string, params: PageParams = {}) =>
    api.get<Page<Invoice>>(`/api/v1/platform/tenants/${organizationId}/invoices`, {
      query: { ...params },
    }),
  issueInvoice: (organizationId: string, paymentTermsDays?: number) =>
    api.post<Invoice>(`/api/v1/platform/tenants/${organizationId}/invoices`, { paymentTermsDays }),
  markPaid: (invoiceId: string, paymentReference?: string) =>
    api.post<Invoice>(`/api/v1/platform/invoices/${invoiceId}/pay`, { paymentReference }),
  voidInvoice: (invoiceId: string, reason: string) =>
    api.post<Invoice>(`/api/v1/platform/invoices/${invoiceId}/void`, { reason }),

  plans: () => api.get<Plan[]>('/api/v1/platform/plans'),
  createPlan: (body: { code: string; terms: PlanTerms }) =>
    api.post<Plan>('/api/v1/platform/plans', body),
  updatePlan: (planId: string, body: { terms: PlanTerms; active: boolean }) =>
    api.put<Plan>(`/api/v1/platform/plans/${planId}`, body),
};

/** The signed-in user's own company: its licence and invoices. */
export const subscriptionApi = {
  mine: () => api.get<Tenant>('/api/v1/subscription'),
  invoices: (params: PageParams = {}) =>
    api.get<Page<Invoice>>('/api/v1/subscription/invoices', { query: { ...params } }),
};

// ---- Assets -----------------------------------------------------------------------------------

export interface AssetSearchParams extends PageParams {
  status?: string;
  assetTypeId?: string;
  locationId?: string;
  employeeId?: string;
  category?: AssetCategory;
  search?: string;
}

export const assetsApi = {
  search: (params: AssetSearchParams) =>
    api.get<Page<Asset>>('/api/v1/assets', { query: { ...params } }),

  get: (assetId: string) => api.get<Asset>(`/api/v1/assets/${assetId}`),

  byTag: (tag: string) => api.get<Asset>(`/api/v1/assets/by-tag/${encodeURIComponent(tag)}`),

  bySerial: (serialNumber: string) =>
    api.get<Asset>('/api/v1/assets/by-serial', { query: { serialNumber } }),

  create: (body: AssetUpsert) => api.post<Asset>('/api/v1/assets', body),

  update: (assetId: string, body: AssetUpsert) =>
    api.put<Asset>(`/api/v1/assets/${assetId}`, body),

  applyTag: (assetId: string, assetTag: string) =>
    api.post<Asset>(`/api/v1/assets/${assetId}/tag`, { assetTag }),

  /**
   * The client names a transition, never a target status. `targetStatus` is meaningful only for
   * the corrective override, which the server gates behind its own permission.
   */
  transition: (
    assetId: string,
    body: { transition: string; targetStatus?: string | null; reason?: string | null; targetLocationId?: string | null; expectedVersion?: number | null },
  ) => api.post<Asset>(`/api/v1/assets/${assetId}/transitions`, body),

  lifecycle: (assetId: string, params: PageParams = {}) =>
    api.get<Page<AssetLifecycleEvent>>(`/api/v1/assets/${assetId}/lifecycle`, { query: { ...params } }),
};

// ---- Employees --------------------------------------------------------------------------------

export interface EmployeeSearchParams extends PageParams {
  status?: string;
  departmentId?: string;
  search?: string;
}

export interface EmployeeInput {
  employeeNumber?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  jobTitle?: string | null;
  departmentId?: string | null;
  locationId?: string | null;
  managerId?: string | null;
  joinedOn?: string | null;
}

export const employeesApi = {
  search: (params: EmployeeSearchParams) =>
    api.get<Page<Employee>>('/api/v1/employees', { query: { ...params } }),

  get: (employeeId: string) => api.get<Employee>(`/api/v1/employees/${employeeId}`),

  create: (body: EmployeeInput) => api.post<Employee>('/api/v1/employees', body),

  update: (employeeId: string, body: Omit<EmployeeInput, 'employeeNumber' | 'joinedOn'>) =>
    api.put<Employee>(`/api/v1/employees/${employeeId}`, body),

  changeStatus: (employeeId: string, status: string, effectiveOn?: string) =>
    api.post<Employee>(`/api/v1/employees/${employeeId}/status`, { status, effectiveOn }),

  /**
   * Uploads a profile photograph.
   *
   * Multipart rather than a base64 string: base64 inflates the payload by a third and puts the
   * image into request logs. The server proves the format from the file's own bytes, so the
   * browser's `File.type` is not what is trusted.
   */
  uploadPhoto: (employeeId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.postForm<Employee>(`/api/v1/employees/${employeeId}/photo`, form);
  },

  removePhoto: (employeeId: string) =>
    api.delete<Employee>(`/api/v1/employees/${employeeId}/photo`),

  /**
   * The photo's path.
   *
   * Not usable as an `<img src>` directly - the endpoint requires the bearer token, which the
   * browser does not send for an image request. `EmployeeAvatar` fetches it through the
   * authenticated client; this exists so the path is written in one place.
   */
  photoPath: (employeeId: string) => `/api/v1/employees/${employeeId}/photo`,
};

// ---- Asset health checks ------------------------------------------------------------------------

export type AssetConditionValue = 'NEW' | 'GOOD' | 'FAIR' | 'POOR' | 'DAMAGED';

export interface AssetHealthCheck {
  id: string;
  assetId: string;
  /** The day the device was inspected (yyyy-mm-dd), which may be earlier than the upload. */
  checkedOn: string;
  condition: AssetConditionValue | null;
  notes: string | null;
  photos: { id: string; contentType: string; sizeBytes: number }[];
  recordedBy: string | null;
  recordedAt: string;
}

/** Dated inspections of one asset, with photographs. Append-only: a correction is a new check. */
export const assetHealthApi = {
  list: (assetId: string, params: PageParams = {}) =>
    api.get<Page<AssetHealthCheck>>(`/api/v1/assets/${assetId}/health-checks`, {
      query: { size: 50, ...params },
    }),

  record: (
    assetId: string,
    input: { checkedOn: string; condition?: string; notes?: string; photos: File[] },
  ) => {
    const form = new FormData();
    form.append('checkedOn', input.checkedOn);
    if (input.condition) form.append('condition', input.condition);
    if (input.notes) form.append('notes', input.notes);
    input.photos.forEach((photo) => form.append('photos', photo));
    return api.postForm<AssetHealthCheck>(`/api/v1/assets/${assetId}/health-checks`, form, {
      // Several phone photos over a site connection can take a while; the default 30s is too short.
      timeoutMs: 120_000,
    });
  },

  /** Protected: fetch through `fetchObjectUrl`, never as a bare `<img src>`. */
  photoPath: (assetId: string, healthCheckId: string, photoId: string) =>
    `/api/v1/assets/${assetId}/health-checks/${healthCheckId}/photos/${photoId}`,
};

// ---- Assignments ------------------------------------------------------------------------------

export const assignmentsApi = {
  list: (params: PageParams & { status?: string; overdueOnly?: boolean }) =>
    api.get<Page<Assignment>>('/api/v1/assignments', { query: { ...params } }),

  mine: (params: PageParams = {}) =>
    api.get<Page<Assignment>>('/api/v1/assignments/mine', { query: { ...params } }),

  get: (assignmentId: string) => api.get<Assignment>(`/api/v1/assignments/${assignmentId}`),

  create: (body: CreateAssignment) => api.post<Assignment>('/api/v1/assignments', body),

  cancel: (assignmentId: string, body: AssignmentAction) =>
    api.post<Assignment>(`/api/v1/assignments/${assignmentId}/cancel`, body),

  recordReturn: (assignmentId: string, body: AssignmentAction) =>
    api.post<Assignment>(`/api/v1/assignments/${assignmentId}/return`, body),
};

// ---- Acceptance -------------------------------------------------------------------------------

export const acceptanceApi = {
  get: (acceptanceId: string) => api.get<Acceptance>(`/api/v1/acceptances/${acceptanceId}`),

  rejectionReasons: () => api.get<RejectionReason[]>('/api/v1/rejection-reasons'),

  /**
   * Multipart: the decision travels as a JSON part alongside the signature and photos, so the
   * decision and its evidence are validated and committed together.
   */
  accept: (assignmentId: string, decision: AcceptDecision, signature: Blob, photos: File[]) => {
    const form = new FormData();
    form.append('decision', new Blob([JSON.stringify(decision)], { type: 'application/json' }));
    form.append('signature', signature, 'signature.png');
    photos.forEach((photo) => form.append('photos', photo, photo.name));
    return api.postForm<Acceptance>(`/api/v1/assignments/${assignmentId}/acceptance`, form);
  },

  reject: (assignmentId: string, decision: RejectDecision, photos: File[]) => {
    const form = new FormData();
    form.append('decision', new Blob([JSON.stringify(decision)], { type: 'application/json' }));
    photos.forEach((photo) => form.append('photos', photo, photo.name));
    return api.postForm<Acceptance>(`/api/v1/assignments/${assignmentId}/rejection`, form);
  },

  /**
   * Returns a short-lived pre-signed URL rather than the bytes. The link expires in minutes, so it
   * is fetched on demand and never stored.
   */
  photoUrl: (acceptanceId: string, photoId: string) =>
    api.get<{ url: string; sha256: string; contentType: string }>(
      `/api/v1/acceptances/${acceptanceId}/photos/${photoId}/download`,
    ),

  signatureUrl: (acceptanceId: string) =>
    api.get<{ url: string; sha256: string }>(
      `/api/v1/acceptances/${acceptanceId}/signature/download`,
    ),
};

// ---- Procurement ------------------------------------------------------------------------------

export const procurementApi = {
  list: (params: PageParams & { status?: string; vendorId?: string; search?: string }) =>
    api.get<Page<ProcurementOrder>>('/api/v1/procurement/orders', { query: { ...params } }),

  get: (orderId: string) => api.get<ProcurementOrder>(`/api/v1/procurement/orders/${orderId}`),

  create: (body: CreateOrder) => api.post<ProcurementOrder>('/api/v1/procurement/orders', body),

  update: (orderId: string, body: CreateOrder) =>
    api.put<ProcurementOrder>(`/api/v1/procurement/orders/${orderId}`, body),

  submit: (orderId: string) =>
    api.post<ProcurementOrder>(`/api/v1/procurement/orders/${orderId}/submit`),

  approve: (orderId: string) =>
    api.post<ProcurementOrder>(`/api/v1/procurement/orders/${orderId}/approve`),

  cancel: (orderId: string, reason: string) =>
    api.post<ProcurementOrder>(`/api/v1/procurement/orders/${orderId}/cancel`, { reason }),

  /**
   * Receiving goods creates assets. The idempotency key is mandatory: a retry after a timeout must
   * not register the same delivery twice.
   */
  receive: (orderId: string, body: ReceiveInput, idempotencyKey: string) =>
    request<ProcurementReceipt>(`/api/v1/procurement/orders/${orderId}/receipts`, {
      method: 'POST',
      body,
      idempotencyKey,
    }),
};

// ---- Preparation ------------------------------------------------------------------------------

export const preparationApi = {
  list: (params: PageParams & { status?: string }) =>
    api.get<Page<PreparationRun>>('/api/v1/preparation/runs', { query: { ...params } }),

  get: (runId: string) => api.get<PreparationRun>(`/api/v1/preparation/runs/${runId}`),

  byAsset: (assetId: string) =>
    api.get<PreparationRun[]>(`/api/v1/preparation/runs/by-asset/${assetId}`),

  start: (body: StartRun) => api.post<PreparationRun>('/api/v1/preparation/runs', body),

  recordTask: (runId: string, taskId: string, body: RecordTask) =>
    api.post<PreparationRun>(`/api/v1/preparation/runs/${runId}/tasks/${taskId}`, body),

  complete: (runId: string, body: { notes?: string | null }) =>
    api.post<PreparationRun>(`/api/v1/preparation/runs/${runId}/complete`, body),

  abandon: (runId: string, body: { reason: string }) =>
    api.post<PreparationRun>(`/api/v1/preparation/runs/${runId}/abandon`, body),
};

// ---- Notifications ----------------------------------------------------------------------------

export const notificationsApi = {
  mine: (params: PageParams & { unreadOnly?: boolean }) =>
    api.get<Page<Notification>>('/api/v1/notifications/mine', { query: { ...params } }),

  unreadCount: () => api.get<{ unread: number }>('/api/v1/notifications/mine/unread-count'),

  markRead: (notificationId: string) =>
    api.post<Notification>(`/api/v1/notifications/${notificationId}/read`),

  markAllRead: () => api.post<{ updated: number }>('/api/v1/notifications/mine/read-all'),

  /** Administrative view across all recipients. Requires NOTIFICATION_ADMIN. */
  all: (params: PageParams & { status?: string }) =>
    api.get<Page<Notification>>('/api/v1/notifications', { query: { ...params } }),
};

// ---- Audit ------------------------------------------------------------------------------------

export interface AuditSearchParams extends PageParams {
  entityType?: string;
  entityId?: string;
  eventType?: string;
  actorUserId?: string;
  from?: string;
  to?: string;
}

export const auditApi = {
  search: (params: AuditSearchParams) =>
    api.get<Page<AuditEvent>>('/api/v1/audit/events', { query: { ...params } }),

  verify: () => api.post<AuditChainReport>('/api/v1/audit/verify'),
};

// ---- Integrations / outbox --------------------------------------------------------------------

export const outboxApi = {
  messages: (params: PageParams & { status?: string; destination?: string }) =>
    api.get<Page<OutboxMessage>>('/api/v1/outbox/messages', { query: { ...params } }),

  stats: () => api.get<OutboxStats>('/api/v1/outbox/stats'),

  replay: (messageId: string, additionalAttempts = 3) =>
    api.post<OutboxMessage>(`/api/v1/outbox/messages/${messageId}/replay`, undefined, {
      query: { additionalAttempts },
    }),

  discard: (messageId: string, reason: string) =>
    api.post<OutboxMessage>(`/api/v1/outbox/messages/${messageId}/discard`, undefined, {
      query: { reason },
    }),
};

// ---- Consumables ------------------------------------------------------------------------------

/** The create payload. Named here because the generated name for it says nothing at a call site. */
export interface ConsumableInput {
  sku: string;
  name: string;
  category: string;
  unit: string;
  description?: string | null;
  reorderLevel: number;
  unitCost?: string | null;
  currency?: string | null;
  supplier?: string | null;
  locationId?: string | null;
  openingQuantity: number;
}

/**
 * Stock held by quantity rather than by identity.
 *
 * Deliberately not part of the asset API: an asset is one identified device with a lifecycle, a
 * consumable is a count of interchangeable things. The two share no workflow.
 */
export const consumablesApi = {
  search: (
    params: PageParams & {
      category?: string;
      locationId?: string;
      lowStockOnly?: boolean;
      includeArchived?: boolean;
      search?: string;
    },
  ) => api.get<Page<Consumable>>('/api/v1/consumables', { query: { ...params } }),

  get: (consumableId: string) => api.get<Consumable>(`/api/v1/consumables/${consumableId}`),

  categories: () => api.get<string[]>('/api/v1/consumables/categories'),

  create: (body: ConsumableInput) => api.post<Consumable>('/api/v1/consumables', body),

  update: (consumableId: string, body: Omit<ConsumableInput, 'sku' | 'openingQuantity'>) =>
    api.put<Consumable>(`/api/v1/consumables/${consumableId}`, body),

  movements: (consumableId: string, params: PageParams) =>
    api.get<Page<ConsumableMovement>>(`/api/v1/consumables/${consumableId}/movements`, {
      query: { ...params },
    }),

  /**
   * Moves stock. `quantity` is a magnitude for every reason but ADJUSTMENT, where it is signed -
   * the server applies the reason's direction, so issuing cannot accidentally add stock.
   */
  move: (
    consumableId: string,
    body: { reason: MovementReason; quantity: number; employeeId?: string; note?: string },
  ) => api.post<ConsumableMovement>(`/api/v1/consumables/${consumableId}/movements`, body),

  archive: (consumableId: string) =>
    api.post<Consumable>(`/api/v1/consumables/${consumableId}/archive`),

  restore: (consumableId: string) =>
    api.post<Consumable>(`/api/v1/consumables/${consumableId}/restore`),
};

// ---- Reports ----------------------------------------------------------------------------------

/** A count against a named thing, ready to plot. */
export interface ReportBucket {
  /** Stable key - an enum name or an id. Safe to build a filter link from. */
  key: string;
  label: string;
  value: number;
}

export interface AssetSummaryReport {
  total: number;
  byStatus: ReportBucket[];
  byType: ReportBucket[];
  byCondition: ReportBucket[];
  byLocation: ReportBucket[];
}

/**
 * Aggregates for the dashboard.
 *
 * These replaced a dozen `size=1` searches whose only purpose was to read a total out of the
 * response envelope - and which could not answer "how many by location" at all without one request
 * per location.
 */
export interface BookValueReport {
  asOf: string;
  assetsCounted: number;
  valuedCount: number;
  unknownCount: number;
  currencies: { currencyCode: string; assetCount: number; purchaseCost: number; bookValue: number; depreciatedValue: number; salvageValue: number }[];
}

export const reportsApi = {
  bookValue: (category?: AssetCategory) => api.get<BookValueReport>('/api/v1/reports/book-value', { query: { category } }),
  assetSummary: (category?: AssetCategory) =>
    api.get<AssetSummaryReport>('/api/v1/reports/asset-summary', { query: { category } }),

  assetScores: (category?: AssetCategory) =>
    api.get<AssetScoresReport>('/api/v1/reports/asset-scores', { query: { category } }),

  warrantyExpiry: (months = 12) =>
    api.get<ReportBucket[]>('/api/v1/reports/warranty-expiry', { query: { months } }),
};

export interface AcceptanceBucket {
  key: string;
  label: string;
  accepted: number;
  rejected: number;
}

export interface SpendReport {
  months: { key: string; label: string; amount: string }[];
  /**
   * Distinct currencies across the assets counted. More than one means the totals mix currencies
   * and are not comparable - the platform holds no exchange rates, so the screen says so rather
   * than showing a number that looks authoritative and is not.
   */
  currencies: string[];
}

export const analyticsApi = {
  acceptanceTrend: (months = 12) =>
    api.get<AcceptanceBucket[]>('/api/v1/reports/acceptance-trend', { query: { months } }),

  spend: (months = 12) => api.get<SpendReport>('/api/v1/reports/spend', { query: { months } }),
};

// ---- Software across the fleet -----------------------------------------------------------------

export const softwareApi = {
  titles: (params: PageParams & { search?: string; sort?: 'name' | 'devices' }) =>
    api.get<Page<SoftwareTitle>>('/api/v1/software', { query: { ...params } }),

  installs: (params: PageParams & { name: string; olderThan?: string }) =>
    api.get<Page<SoftwareInstall>>('/api/v1/software/installs', { query: { ...params } }),

  versions: (name: string) =>
    api.get<SoftwareVersionCount[]>('/api/v1/software/versions', { query: { name } }),

  changes: (params: PageParams & { search?: string; type?: string }) =>
    api.get<Page<FleetSoftwareChange>>('/api/v1/software/changes', { query: { ...params } }),
};

// ---- Device location ---------------------------------------------------------------------------

export const locationApi = {
  /** Where one device is and has been. Every call is audited on the server. */
  device: (endpointId: string) => api.get<DeviceLocation>(`/api/v1/endpoints/${endpointId}/location`),

  /** Every device's latest known place. Audited. */
  fleet: () => api.get<FleetLocation>('/api/v1/locations'),

  settings: () => api.get<LocationTrackingSettings>('/api/v1/locations/settings'),

  changeSettings: (body: LocationTrackingRequest) =>
    api.put<LocationTrackingSettings>('/api/v1/locations/settings', body),
};

// ---- Endpoints (osquery agents) ----------------------------------------------------------------

export interface DeviceAlert {
  id: string;
  title: string;
  body: string;
  createdBy: string;
  createdAt: string;
  /** Null until the device collected it on a check-in; that absence is what "pending" means. */
  deliveredAt: string | null;
}

export interface UninstallAuthorization {
  id: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
  failedAttempts: number;
}

export interface DeviceMonitoringSettings {
  activeRefreshSeconds: number;
  offlineMinutes: number;
  warningHours: number;
  alertHours: number;
}

export const endpointsApi = {
  monitoringSettings: (id: string) => api.get<DeviceMonitoringSettings>(`/api/v1/endpoints/${encodeURIComponent(id)}/monitoring-settings`),
  updateMonitoringSettings: (id: string, body: DeviceMonitoringSettings) =>
    api.put<DeviceMonitoringSettings>(`/api/v1/endpoints/${encodeURIComponent(id)}/monitoring-settings`, body),
  summary: () => api.get<EndpointSummary>('/api/v1/endpoints/summary'),

  /**
   * Queues a message for a device. It arrives on the device's next check-in — within fifteen
   * minutes of a phone that is on and connected — so this confirms it was recorded, not seen.
   */
  sendDeviceAlert: (endpointId: string, title: string, body: string) =>
    api.post<DeviceAlert>(`/api/v1/endpoints/${endpointId}/alerts`, { title, body }),

  deviceAlerts: (endpointId: string) =>
    api.get<DeviceAlert[]>(`/api/v1/endpoints/${endpointId}/alerts`),

  /** Issues a one-time code allowing the agent to be removed. Shown once, never retrievable. */
  authorizeUninstall: (endpointId: string) =>
    api.post<{ code: string; expiresAt: string }>(
      `/api/v1/endpoints/${endpointId}/uninstall-authorization`,
    ),

  uninstallAuthorizations: (endpointId: string) =>
    api.get<UninstallAuthorization[]>(`/api/v1/endpoints/${endpointId}/uninstall-authorization`),

  /** One device with its memory modules. */
  detail: (endpointId: string) => api.get<EndpointDetail>(`/api/v1/endpoints/${endpointId}`),

  /** Software installed on a device, as its agent last reported it. */
  software: (endpointId: string, params: PageParams & { search?: string }) =>
    api.get<Page<InstalledSoftware>>(`/api/v1/endpoints/${endpointId}/software`, { query: { ...params } }),

  /** What was installed, updated or removed on a device, newest first. */
  softwareChanges: (endpointId: string, params: PageParams) =>
    api.get<Page<SoftwareChange>>(`/api/v1/endpoints/${endpointId}/software/changes`, { query: { ...params } }),

  /** Everything else the agent reports, by category - the categories that apply to its platform. */
  facts: (endpointId: string) =>
    api.get<EndpointFactSection[]>(`/api/v1/endpoints/${endpointId}/facts`),

  /** The devices reporting as one asset - normally one. */
  byAsset: (assetId: string) =>
    api.get<EndpointDevice[]>(`/api/v1/endpoints/by-asset/${assetId}`),

  list: (params: PageParams & { matched?: boolean; platform?: string; staleOnly?: boolean; search?: string }) =>
    api.get<Page<EndpointDevice>>('/api/v1/endpoints', { query: { ...params } }),

  /** Links a device to an asset. A null assetId clears the link. */
  link: (endpointId: string, assetId: string | null) =>
    api.post<EndpointDevice>(`/api/v1/endpoints/${endpointId}/link`, { assetId }),

  /** The QR payload that provisions a company-owned phone as a fully managed device. */
  mobileProvisioning: (secret: string, wifiSsid?: string, wifiPassword?: string) =>
    api.post<{ payload: string }>('/api/v1/endpoints/mobile/provisioning',
      { secret, wifiSsid: wifiSsid || undefined, wifiPassword: wifiPassword || undefined }),

  enrollSecrets: () => api.get<EnrollSecret[]>('/api/v1/endpoints/enroll-secrets'),

  /** The response carries the secret's value - the only time it is available. */
  createEnrollSecret: (name: string) =>
    api.post<IssuedEnrollSecret>('/api/v1/endpoints/enroll-secrets', { name }),

  revokeEnrollSecret: (secretId: string) =>
    api.post<EnrollSecret>(`/api/v1/endpoints/enroll-secrets/${secretId}/revoke`),

  /** Saves the installer program for one platform, carrying the company ID and this secret. */
  downloadInstaller: (platform: AgentPlatform, secret: string) =>
    downloadFile('/api/v1/endpoints/installers', { platform, secret },
      `dam-agent-installer-${platform.toLowerCase()}${platform === 'WINDOWS' ? '.exe' : '.tar.gz'}`),

  /** Downloads the Android mobile agent APK package. */
  downloadAndroidAgent: () =>
    downloadGetFile('/api/v1/agents/install/android', 'dam-mobile-agent.apk'),

  installerStatus: () => api.get<InstallerBuildStatus>('/api/v1/endpoints/installers/status'),

  /** Starts a background build of the installer programs; poll installerStatus. */
  rebuildInstallers: () => api.post<InstallerBuildStatus>('/api/v1/endpoints/installers/rebuild'),
};

export interface InstallerBuildStatus {
  /** The installer programs are built and can be downloaded. */
  ready: boolean;
  building: boolean;
  /** This server builds them itself; false where they come ready-built in the image. */
  canBuild: boolean;
  builtAt: string | null;
  error: string | null;
}

export interface RegisteredEndpoint {
  id: string;
  /** COMPUTER for a laptop, desktop or server; MOBILE for a phone or tablet. */
  kind: 'COMPUTER' | 'MOBILE';
  osqueryId: string | null;
  velociraptorId: string | null;
  assetId: string | null;
  hostname: string | null;
  hardwareSerial: string | null;
  platform: string | null;
  platformVersion: string | null;
  ipAddress: string | null;
  lastSeenAt: string | null;
  externalClientId: string | null;
  health: 'ONLINE' | 'OFFLINE';
  osqueryHealth: 'ONLINE' | 'OFFLINE' | null;
  velociraptorHealth: 'ONLINE' | 'OFFLINE' | null;
}
export const registeredEndpointsApi = {
  list: (params: PageParams & { search?: string; status?: 'ALL' | 'ONLINE' | 'OFFLINE' }) =>
    api.get<Page<RegisteredEndpoint>>('/api/v1/registered-endpoints', { query: { ...params } }),
};

// ---- Velociraptor endpoint response -----------------------------------------------------------

export interface VelociraptorEndpoint {
  id: string;
  asset_id: string | null;
  provider: 'VELOCIRAPTOR';
  external_client_id: string;
  hostname: string | null;
  platform: string | null;
  platform_version: string | null;
  architecture: string | null;
  agent_version: string | null;
  ip_address: string | null;
  mac_address: string | null;
  last_seen_at: string | null;
  connection_status: 'ONLINE' | 'OFFLINE' | 'UNKNOWN';
  metadata: { labels?: string[] };
}
export interface RemoteCommandDefinition {
  name: string;
  description: string;
  riskLevel: string;
  requiredPermission: string;
  parameterSchema: Record<string, string>;
  timeoutSeconds: number;
  approvalRequired: boolean;
}
export interface RemoteCommand {
  id: string;
  asset_id: string;
  endpoint_integration_id: string;
  requested_by: string;
  requested_by_name?: string;
  command_type: string;
  command_parameters: Record<string, unknown>;
  status: string;
  requested_at: string;
  started_at: string | null;
  completed_at: string | null;
  exit_code: number | null;
  stdout: string | null;
  stderr: string | null;
  error_message: string | null;
  timeout_seconds: number;
  approval_required: boolean;
  approved_by: string | null;
  approved_by_name?: string | null;
  approved_at: string | null;
}
export const velociraptorApi = {
  downloadClientConfig: () => downloadGetFile('/api/v1/velociraptor/client-config', 'client.config.yaml'),
  clientConfigText: () => fetchTextFile('/api/v1/velociraptor/client-config'),
  status: () => api.get<{ enabled: boolean; configured: boolean }>('/api/v1/velociraptor/status'),
  sync: () => api.post<{ synchronized: number }>('/api/v1/velociraptor/sync'),
  endpoints: () => api.get<VelociraptorEndpoint[]>('/api/v1/velociraptor/endpoints'),
  commands: () => api.get<Record<string, RemoteCommandDefinition>>('/api/v1/velociraptor/commands'),
  history: (endpointId: string) => api.get<RemoteCommand[]>(`/api/v1/velociraptor/endpoints/${endpointId}/commands`),
  link: (endpointId: string, assetId: string | null) =>
    api.put<{ status: string }>(`/api/v1/velociraptor/endpoints/${endpointId}/asset`, { assetId }),
  request: (endpointId: string, commandType: string, commandParameters: Record<string, unknown>, confirmed = false) =>
    api.post<{ id: string }>(`/api/v1/velociraptor/endpoints/${endpointId}/commands`, { commandType, commandParameters, confirmed }),
  approve: (commandId: string, approved: boolean) =>
    api.post<{ status: string }>(`/api/v1/velociraptor/commands/${commandId}/approval`, { approved }),
  remoteSession: (endpointId: string) =>
    api.get<RemoteSessionState>(`/api/v1/velociraptor/endpoints/${endpointId}/remote-session`),
  startRemoteSession: (endpointId: string, type: RemoteSession['session_type']) =>
    api.post<{ id: string }>(`/api/v1/velociraptor/endpoints/${endpointId}/remote-session`, { type }),
  endRemoteSession: (sessionId: string) => api.delete<void>(`/api/v1/velociraptor/remote-sessions/${sessionId}`),
  remoteSessionHistory: (endpointId: string) =>
    api.get<RemoteSession[]>(`/api/v1/velociraptor/endpoints/${endpointId}/remote-sessions`),
  setRemoteUnattended: (endpointId: string, unattended: boolean) =>
    api.put<void>(`/api/v1/velociraptor/endpoints/${endpointId}/remote-session/settings`, { unattended }),
  resetRemoteIdentity: (endpointId: string) =>
    api.delete<void>(`/api/v1/velociraptor/endpoints/${endpointId}/remote-session/identity`),
};

export interface RemoteSession {
  id: string;
  session_type: 'DESKTOP' | 'TERMINAL';
  status: 'STARTING' | 'ACTIVE' | 'ENDED' | 'FAILED';
  requested_by: string;
  requested_by_name: string;
  requested_at: string;
  expires_at: string;
  ended_at: string | null;
  error_message: string | null;
  /** Present only for the operator who opened the session, and only while it is active. Never in history. */
  share_url?: string | null;
}

export interface RemoteSessionState {
  enabled: boolean;
  /** Nobody is at this device: its sessions notify instead of asking. */
  unattended?: boolean;
  /** The device's session agent identity is pinned to this endpoint. */
  identityPinned?: boolean;
  session?: RemoteSession;
}

// ---- Device groups ----------------------------------------------------------------------------

export const endpointGroupsApi = {
  list: () => api.get<DeviceGroup[]>('/api/v1/endpoint-groups'),
  get: (groupId: string) => api.get<DeviceGroupDetail>(`/api/v1/endpoint-groups/${groupId}`),
  create: (name: string, description?: string) =>
    api.post<DeviceGroup>('/api/v1/endpoint-groups', { name, description }),
  update: (groupId: string, name: string, description?: string) =>
    api.put<DeviceGroup>(`/api/v1/endpoint-groups/${groupId}`, { name, description }),
  remove: (groupId: string) => api.delete<void>(`/api/v1/endpoint-groups/${groupId}`),
  /** Policies aimed at the group follow the change. */
  changeMembers: (groupId: string, add: string[], remove: string[]) =>
    api.post<DeviceGroupDetail>(`/api/v1/endpoint-groups/${groupId}/members`, { add, remove }),
};

// ---- Application-control policies ---------------------------------------------------------------

export const policiesApi = {
  settings: () => api.get<PolicySettings>('/api/v1/policies/settings'),
  list: (status?: PolicyStatus) => api.get<PolicySummary[]>('/api/v1/policies', { query: { status } }),
  get: (policyId: string) => api.get<Policy>(`/api/v1/policies/${policyId}`),
  create: (input: PolicyInput) => api.post<Policy>('/api/v1/policies', input),
  update: (policyId: string, input: PolicyInput) => api.put<Policy>(`/api/v1/policies/${policyId}`, input),
  /** Withdraws the policy from every device. History is kept. */
  retire: (policyId: string) => api.delete<Policy>(`/api/v1/policies/${policyId}`),

  /** Changes nothing: errors, warnings, conflicts, reach and whether enforcement is allowed. */
  validate: (policyId: string) => api.post<PolicyValidation>(`/api/v1/policies/${policyId}/validate`),
  publish: (policyId: string) => api.post<PolicyValidation>(`/api/v1/policies/${policyId}/publish`),
  /** Deploys the published version in AUDIT mode. */
  deploy: (policyId: string) => api.post<PolicyDeployResult>(`/api/v1/policies/${policyId}/deploy`),
  audit: (policyId: string, note?: string) =>
    api.post<PolicyDeployResult>(`/api/v1/policies/${policyId}/audit`, { note }),
  /** confirmDeviceCount must equal the device count validate reported. */
  enforce: (policyId: string, confirmDeviceCount: number, note?: string) =>
    api.post<PolicyDeployResult>(`/api/v1/policies/${policyId}/enforce`, { confirmDeviceCount, note }),
  rollback: (policyId: string, toVersion: number, confirmDeviceCount?: number, note?: string) =>
    api.post<PolicyDeployResult>(`/api/v1/policies/${policyId}/rollback`,
      { toVersion, confirmDeviceCount, note }),

  versions: (policyId: string) =>
    api.get<PolicyVersionSummary[]>(`/api/v1/policies/${policyId}/versions`),
  version: (policyId: string, number: number) =>
    api.get<PolicyVersion>(`/api/v1/policies/${policyId}/versions/${number}`),

  policyDeployments: (policyId: string, params: PageParams & { status?: DeploymentStatus; activeOnly?: boolean }) =>
    api.get<Page<PolicyDeployment>>(`/api/v1/policies/${policyId}/deployments`, { query: { ...params } }),
  deployments: (params: PageParams & { status?: DeploymentStatus; activeOnly?: boolean }) =>
    api.get<Page<PolicyDeployment>>('/api/v1/policies/deployments', { query: { ...params } }),
  /** Sends the deployment to its device again: the controlled answer to drift or a failure. */
  reconcile: (deploymentId: string) =>
    api.post<PolicyDeployment>(`/api/v1/policies/deployments/${deploymentId}/reconcile`),

  policyEvents: (policyId: string, params: PageParams) =>
    api.get<Page<PolicyEvent>>(`/api/v1/policies/${policyId}/events`, { query: { ...params } }),
  events: (params: PageParams & { endpointId?: string }) =>
    api.get<Page<PolicyEvent>>('/api/v1/policies/events', { query: { ...params } }),

  byDevice: (endpointId: string) => api.get<DevicePolicies>(`/api/v1/policies/by-device/${endpointId}`),
};

// ---- Network discovery ------------------------------------------------------------------------

export interface DiscoveryRange {
  id: string;
  cidr: string;
  label: string | null;
  enabled: boolean;
  authorisedByUserId: string;
  authorisedAt: string;
  authorisationNote: string | null;
}

export interface DiscoveredDevice {
  id: string;
  ipAddress: string;
  sysName: string | null;
  sysDescr: string | null;
  sysObjectId: string | null;
  sysContact: string | null;
  sysLocation: string | null;
  respondedVia: 'ICMP_ONLY' | 'SNMP';
  assetId: string | null;
  matched: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface DiscoveryRun {
  id: string;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  startedAt: string;
  finishedAt: string | null;
  startedByUserId: string | null;
  addressesProbed: number;
  devicesFound: number;
  snmpAnswered: number;
  failureReason: string | null;
}

export interface DiscoverySummary {
  total: number;
  unmatched: number;
  byResponse: Record<string, number>;
  authorisedRanges: number;
  addressesCovered: number;
  lastScanAt: string | null;
  lastScanStatus: string | null;
  enabled: boolean;
  snmpConfigured: boolean;
  /** Why a scan cannot run, or null if it can. Already a sentence somebody can act on. */
  blockedReason: string | null;
}

export interface SiteCollector {
  id: string;
  name: string;
  enrolled: boolean;
  revoked: boolean;
  enrolmentCode?: string | null;
  enrolmentExpiresAt: string | null;
  enrolledAt: string | null;
  lastSeenAt: string | null;
  agentVersion: string | null;
  platform: string | null;
  hostName: string | null;
  createdAt: string;
}

/** The response that carries the collector and its enrolment code. */
export interface CreatedSiteCollector {
  collector: SiteCollector;
  enrolmentCode: string;
}

/** A collector program this deployment can hand out, and the fingerprint of the exact file. */
export interface CollectorDownload {
  platform: string;
  label: string;
  sizeBytes: number;
  sha256: string;
}

/** Where the browser fetches a build. Unauthenticated: the file carries no credential. */
export const collectorDownloadUrl = (platform: string) =>
  `/api/agent/v1/install/collector/${platform}`;

export const discoveryApi = {
  summary: () => api.get<DiscoverySummary>('/api/v1/discovery/summary'),

  ranges: () => api.get<DiscoveryRange[]>('/api/v1/discovery/ranges'),

  authoriseRange: (body: { cidr: string; label?: string; note?: string }) =>
    api.post<DiscoveryRange>('/api/v1/discovery/ranges', body),

  revokeRange: (rangeId: string) => api.delete<void>(`/api/v1/discovery/ranges/${rangeId}`),

  devices: (params: PageParams & { matched?: boolean; search?: string }) =>
    api.get<Page<DiscoveredDevice>>('/api/v1/discovery/devices', { query: { ...params } }),

  runs: (params: PageParams) =>
    api.get<Page<DiscoveryRun>>('/api/v1/discovery/runs', { query: { ...params } }),

  scanNow: () => api.post<DiscoveryRun>('/api/v1/discovery/scan'),

  collectors: () => api.get<SiteCollector[]>('/api/v1/discovery/collectors'),

  collectorDownloads: () =>
    api.get<CollectorDownload[]>('/api/v1/discovery/collectors/downloads'),

  addCollector: (body: { name: string; customCode?: string }) =>
    api.post<CreatedSiteCollector>('/api/v1/discovery/collectors', body),

  updateCollector: (collectorId: string, body: { name: string }) =>
    api.put<SiteCollector>(`/api/v1/discovery/collectors/${collectorId}`, body),

  reissueCollectorCode: (collectorId: string, body?: { customCode?: string }) =>
    api.post<CreatedSiteCollector>(`/api/v1/discovery/collectors/${collectorId}/reissue`, body ?? {}),

  deleteCollector: (collectorId: string) =>
    api.delete<void>(`/api/v1/discovery/collectors/${collectorId}/delete`),

  revokeCollector: (collectorId: string) =>
    api.delete<void>(`/api/v1/discovery/collectors/${collectorId}`),
};

// ---- Bulk import ------------------------------------------------------------------------------

export interface ImportProblem {
  /** Line in the file; the header is line 1, matching a spreadsheet. */
  lineNumber: number;
  column: string | null;
  value: string | null;
  message: string;
}

export interface ValidationReport {
  jobId: string;
  kind: string;
  fileName: string;
  totalRows: number;
  createCount: number;
  updateCount: number;
  errorCount: number;
  canApply: boolean;
  problems: ImportProblem[];
  /** Set only when the file could not be read as a table at all. */
  failureReason: string | null;
}

export interface ImportJob {
  id: string;
  kind: string;
  status: 'VALIDATED' | 'APPLIED' | 'FAILED';
  fileName: string;
  fileSizeBytes: number;
  fileSha256: string;
  totalRows: number;
  createCount: number;
  updateCount: number;
  errorCount: number;
  appliedAt: string | null;
  appliedByUserId: string | null;
  failureReason: string | null;
  createdAt: string;
}

/**
 * Bulk import.
 *
 * The file is deliberately sent twice - once to check, once to apply. The server keeps only its
 * SHA-256, never the file, because it carries personal data; the digest is what proves the second
 * upload is the one that was reviewed.
 */
export const importsApi = {
  validate: (kind: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.postForm<ValidationReport>(`/api/v1/imports/${kind}/validate`, form);
  },

  apply: (jobId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.postForm<ValidationReport>(`/api/v1/imports/${jobId}/apply`, form);
  },

  history: (params: PageParams & { kind?: string }) =>
    api.get<Page<ImportJob>>('/api/v1/imports', { query: { ...params } }),

  errors: (jobId: string, params: PageParams) =>
    api.get<Page<ImportProblem>>(`/api/v1/imports/${jobId}/errors`, { query: { ...params } }),

  /**
   * Saves the blank template.
   *
   * Fetched rather than linked: the endpoint needs the access token, which lives in memory only,
   * so a plain `<a href>` would arrive unauthenticated. The client returns non-JSON as text, and
   * CSV is text, so the response becomes a Blob here without any special handling.
   */
  downloadTemplate: async (kind: string) => {
    const csv = await api.get<string>(`/api/v1/imports/${kind}/template`);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${kind}-template.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  },
};

// ---- Service desk -----------------------------------------------------------------------------

export type TicketKind = 'INCIDENT' | 'SERVICE_REQUEST';
export type TicketPriority = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
export type TicketStatus =
  | 'NEW'
  | 'TRIAGED'
  | 'IN_PROGRESS'
  | 'ON_HOLD'
  | 'RESOLVED'
  | 'CLOSED'
  | 'CANCELLED';

export interface Ticket {
  id: string;
  reference: string;
  kind: TicketKind;
  status: TicketStatus;
  priority: TicketPriority;
  category: string | null;
  subject: string;
  description: string | null;
  requesterEmployeeId: string | null;
  raisedByUserId: string | null;
  assignedToUserId: string | null;
  assetId: string | null;
  locationId: string | null;
  source: string;
  respondBy: string | null;
  resolveBy: string | null;
  firstRespondedAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  resolution: string | null;
  /** The resolution deadline has passed and the ticket is still open. */
  breaching: boolean;
  /** Minutes until the resolution deadline; negative when past, null when not running. */
  minutesToResolve: number | null;
  /** The moves legal from here. The interface offers exactly these and no others. */
  allowedTransitions: TicketStatus[];
  createdAt: string;
  updatedAt: string;
}

export interface TicketComment {
  id: string;
  authorUserId: string | null;
  body: string;
  internal: boolean;
  createdAt: string;
}

export interface DeskSummary {
  open: number;
  unassigned: number;
  breaching: number;
  byStatus: Record<string, number>;
  openByPriority: Record<string, number>;
}

export interface SlaTarget {
  priority: TicketPriority;
  respondWithinMinutes: number;
  resolveWithinMinutes: number;
}

/**
 * The service desk.
 *
 * Status is changed through transitions, never by setting a field: the server owns the state
 * machine, and each ticket carries the moves currently legal so the interface can offer exactly
 * those.
 */
export interface TicketAssignee {
  id: string;
  displayName: string;
  email: string;
}

export const ticketsApi = {
  search: (
    params: PageParams & {
      status?: string;
      kind?: string;
      priority?: string;
      assignedTo?: string;
      assetId?: string;
      openOnly?: boolean;
      search?: string;
    },
  ) => api.get<Page<Ticket>>('/api/v1/tickets', { query: { ...params } }),

  assignees: () => api.get<TicketAssignee[]>('/api/v1/tickets/assignees'),

  get: (ticketId: string) => api.get<Ticket>(`/api/v1/tickets/${ticketId}`),

  summary: () => api.get<DeskSummary>('/api/v1/tickets/summary'),

  sla: () => api.get<SlaTarget[]>('/api/v1/tickets/sla'),

  raise: (body: {
    kind: TicketKind;
    subject: string;
    description?: string;
    priority?: TicketPriority;
    category?: string;
    requesterEmployeeId?: string;
    assetId?: string;
    locationId?: string;
  }) => api.post<Ticket>('/api/v1/tickets', body),

  assign: (ticketId: string, assigneeUserId: string | null) =>
    api.post<Ticket>(`/api/v1/tickets/${ticketId}/assign`, { assigneeUserId }),

  changeStatus: (ticketId: string, status: TicketStatus) =>
    api.post<Ticket>(`/api/v1/tickets/${ticketId}/status`, { status }),

  changePriority: (ticketId: string, priority: TicketPriority) =>
    api.post<Ticket>(`/api/v1/tickets/${ticketId}/priority`, { priority }),

  resolve: (ticketId: string, resolution: string) =>
    api.post<Ticket>(`/api/v1/tickets/${ticketId}/resolve`, { resolution }),

  close: (ticketId: string) => api.post<Ticket>(`/api/v1/tickets/${ticketId}/close`),

  comments: (ticketId: string) =>
    api.get<TicketComment[]>(`/api/v1/tickets/${ticketId}/comments`),

  comment: (ticketId: string, body: string, internal = false) =>
    api.post<TicketComment>(`/api/v1/tickets/${ticketId}/comments`, { body, internal }),
};

// ---- Maintenance ------------------------------------------------------------------------------

/**
 * Work orders and preventive schedules. Planning a schedule needs MAINTENANCE_PLAN; working an
 * order needs MAINTENANCE_WRITE.
 */
export const maintenanceApi = {
  summary: () => api.get<MaintenanceSummary>('/api/v1/maintenance/summary'),

  workOrders: (
    params: PageParams & {
      status?: WorkOrderStatus;
      kind?: WorkOrderKind;
      assetId?: string;
      contractId?: string;
      outstandingOnly?: boolean;
      overdueOnly?: boolean;
      search?: string;
    },
  ) => api.get<Page<WorkOrder>>('/api/v1/maintenance/work-orders', { query: { ...params } }),

  workOrder: (id: string) => api.get<WorkOrder>(`/api/v1/maintenance/work-orders/${id}`),

  raise: (body: RaiseWorkOrder) => api.post<WorkOrder>('/api/v1/maintenance/work-orders', body),

  update: (id: string, body: WorkOrderFields) =>
    api.put<WorkOrder>(`/api/v1/maintenance/work-orders/${id}`, body),

  recordDowntime: (id: string, body: Downtime) =>
    api.put<WorkOrder>(`/api/v1/maintenance/work-orders/${id}/downtime`, body),

  start: (id: string) => api.post<WorkOrder>(`/api/v1/maintenance/work-orders/${id}/start`),

  hold: (id: string) => api.post<WorkOrder>(`/api/v1/maintenance/work-orders/${id}/hold`),

  complete: (id: string, body: CompleteWorkOrder) =>
    api.post<WorkOrder>(`/api/v1/maintenance/work-orders/${id}/complete`, body),

  cancel: (id: string, reason: string) =>
    api.post<WorkOrder>(`/api/v1/maintenance/work-orders/${id}/cancel`, { reason }),

  schedules: (
    params: PageParams & {
      assetId?: string;
      includePaused?: boolean;
      dueWithinDays?: number;
      search?: string;
    },
  ) => api.get<Page<MaintenanceSchedule>>('/api/v1/maintenance/schedules', { query: { ...params } }),

  createSchedule: (body: CreateSchedule) =>
    api.post<MaintenanceSchedule>('/api/v1/maintenance/schedules', body),

  updateSchedule: (id: string, body: UpdateSchedule) =>
    api.put<MaintenanceSchedule>(`/api/v1/maintenance/schedules/${id}`, body),

  pauseSchedule: (id: string) =>
    api.post<MaintenanceSchedule>(`/api/v1/maintenance/schedules/${id}/pause`),

  resumeSchedule: (id: string) =>
    api.post<MaintenanceSchedule>(`/api/v1/maintenance/schedules/${id}/resume`),

  openNow: (id: string) =>
    api.post<WorkOrder>(`/api/v1/maintenance/schedules/${id}/work-order`),
};

// ---- Warranty & AMC ---------------------------------------------------------------------------

export const contractsApi = {
  summary: () => api.get<ContractSummary>('/api/v1/contracts/summary'),

  search: (
    params: PageParams & {
      kind?: ContractKind;
      vendorId?: string;
      assetId?: string;
      standing?: ContractStanding;
      search?: string;
    },
  ) => api.get<Page<ServiceContract>>('/api/v1/contracts', { query: { ...params } }),

  get: (id: string) => api.get<ServiceContract>(`/api/v1/contracts/${id}`),

  successor: (id: string) => api.get<ServiceContract>(`/api/v1/contracts/${id}/successor`),

  covering: (assetId: string) =>
    api.get<ServiceContract[]>(`/api/v1/contracts/covering/${assetId}`),

  create: (body: CreateContract) => api.post<ServiceContract>('/api/v1/contracts', body),

  update: (id: string, body: ContractFields) =>
    api.put<ServiceContract>(`/api/v1/contracts/${id}`, body),

  cover: (id: string, assetIds: string[]) =>
    api.put<ServiceContract>(`/api/v1/contracts/${id}/assets`, { assetIds }),

  renew: (id: string, body: RenewContract) =>
    api.post<ServiceContract>(`/api/v1/contracts/${id}/renew`, body),

  cancel: (id: string, reason: string) =>
    api.post<ServiceContract>(`/api/v1/contracts/${id}/cancel`, { reason }),
};
